import React, { useEffect, useContext, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import StudentContext from '../../context/StudentContext'
import generateStrongPassword from './Strongpassword'
import { copyToClipboard } from '../utils/Utilsfunc'
import AlertContext from '../../context/AlertContext'
import { useEmail } from '../../context/EmailContext'

const Signup = () => {
	const history = useNavigate()
	const host = process.env.REACT_APP_BACKEND_URL
	const context = useContext(StudentContext)
	const { showAlert } = useContext(AlertContext)
	const { addStudent } = context;
	const [students, setstudents] = useState({ name: "", email: "", gender: "", password: "", address: "", phone: "", parentsphone: "", photo: "", documentid: "", role: "Student" })
	const { sendEmail } = useEmail();
	const [showSpinner, setShowSpinner] = useState(false);
	const [isPhoneAvailable, setIsPhoneAvailable] = useState(true); // State for phone number availability
	const [isEmailAvailable, setIsEmailAvailable] = useState(true); // State for email availability
	const debounceDelay = 1000; // Debounce delay in milliseconds
	
	// Form validation states
	const [errors, setErrors] = useState({});
	
	// Real-time validation states (keeping these for specific real-time checks)
	const [isCheckingEmailAvailability, setIsCheckingEmailAvailability] = useState(false);
	const [isCheckingPhoneAvailability, setIsCheckingPhoneAvailability] = useState(false);

	useEffect(() => {
		const checkAuthentication = async () => {
			const storedToken = localStorage.getItem('token');
			const expiryTime = localStorage.getItem('expiryTime');

			// Check if token exists and has not expired
			if (storedToken && new Date().getTime() < parseInt(expiryTime, 10)) {
				showAlert("You are already logged in.", "info");
				history('/profile'); // Redirect to home if already logged in
			}
			// Function to check if the token is still valid
			const isTokenValid = () => {
				return Boolean(localStorage.getItem('token'));
			};
			// Check if the token exists and is valid
			if (isTokenValid()) {
				showAlert("You are already logged in.", "info");
				history('/profile'); // Redirect to home if already logged in
			}
		};

		checkAuthentication();
	}, [history, showAlert]);

	// Comprehensive form validation
	const validateForm = () => {
		const newErrors = {};

		// Name validation
		if (!students.name.trim()) {
			newErrors.name = "Full name is required";
		} else if (students.name.trim().length < 2) {
			newErrors.name = "Name must be at least 2 characters";
		} else if (!/^[a-zA-Z\s]+$/.test(students.name.trim())) {
			newErrors.name = "Name can only contain letters and spaces";
		}

		// Email validation
		const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
		if (!students.email.trim()) {
			newErrors.email = "Email is required";
		} else if (!emailRegex.test(students.email)) {
			newErrors.email = "Please enter a valid email address";
		}

		// Password validation
		if (!students.password) {
			newErrors.password = "Password is required";
		} else if (students.password.length < 6) {
			newErrors.password = "Password must be at least 6 characters";
		} else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(students.password)) {
			newErrors.password = "Password must contain at least one uppercase letter, one lowercase letter, and one number";
		}

		// Gender validation
		if (!students.gender) {
			newErrors.gender = "Please select a gender";
		}

		// Address validation
		if (!students.address.trim()) {
			newErrors.address = "Address is required";
		} else if (students.address.trim().length < 10) {
			newErrors.address = "Please provide a complete address (at least 10 characters)";
		}

		// Phone validation
		const phoneRegex = /^\d{10}$/;
		if (!students.phone) {
			newErrors.phone = "Phone number is required";
		} else if (!phoneRegex.test(students.phone)) {
			newErrors.phone = "Please enter a valid 10-digit phone number";
		}

		// Parent phone validation
		if (!students.parentsphone) {
			newErrors.parentsphone = "Parent/Guardian phone is required";
		} else if (!phoneRegex.test(students.parentsphone)) {
			newErrors.parentsphone = "Please enter a valid 10-digit phone number";
		} else if (students.parentsphone === students.phone) {
			newErrors.parentsphone = "Parent phone number should be different from your phone number";
		}

		// File validation
		if (!students.photo) {
			newErrors.photo = "Photo is required";
		}

		if (!students.documentid) {
			newErrors.documentid = "Valid ID proof is required";
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleClick = async (e) => {
		e.preventDefault();
		
		// Validate form before submission
		if (!validateForm()) {
			showAlert("Please fix the errors in the form", "danger");
			return;
		}

		// Check if email and phone are available
		if (!isEmailAvailable || !isPhoneAvailable) {
			showAlert("Email or phone number already exists", "danger");
			return;
		}

		setShowSpinner(true)
		const { name, email, gender, password, address, phone, parentsphone, photo, documentid, role } = students;

		try {
			const result = await addStudent(name, email, gender, password, address, phone, parentsphone, photo, documentid, role);

			if (!result.success) {
				showAlert(`Registration failed: ${result.message}`, "danger");
				setShowSpinner(false);
				return;
			}

			setShowSpinner(false)
			const attendanceCode = result.attendanceCode;
			showAlert("Welcome to BookBuddy! Registration successful.", "success");

			// Send welcome email
			const welcomeEmailHTML = `
				<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">
				<html dir="ltr" lang="en">
				<head>
					<meta content="text/html; charset=UTF-8" http-equiv="Content-Type" />
				</head>
				<body style="background-color:rgb(255,255,255);margin-top:auto;margin-bottom:auto;margin-left:auto;margin-right:auto;font-family:ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, &quot;Segoe UI&quot;, Roboto, &quot;Helvetica Neue&quot;, Arial, &quot;Noto Sans&quot;, sans-serif;">
					<div style="max-width:465px;border:1px solid #eaeaea;border-radius:4px;margin:40px auto;padding:20px;">
						<div style="text-align:center;margin:32px 0;">
							<img alt="BookBuddy" height="40" src="https://bookbuddy.co.in/wp-content/uploads/2023/02/Background.png" style="display:block;outline:none;border:none;text-decoration:none;margin:0 auto;" width="130" />
						</div>
						<h1 style="color:rgb(0,0,0);font-size:24px;font-weight:400;text-align:center;margin:30px 0 8px;">Welcome to <strong>BookBuddy</strong></h1>
						<p style="font-size:14px;line-height:24px;margin:16px 0;color:rgb(0,0,0);text-align:center;">Library &amp; Co-Study Zone</p>
						<p style="font-size:14px;line-height:24px;margin:16px 0;color:rgb(0,0,0);">Hello <strong>${students.name}</strong>,</p>
						<p style="font-size:14px;line-height:24px;margin:16px 0;color:rgb(0,0,0);">Welcome to BookBuddy! Your registration has been successful.</p>
						<p style="font-size:14px;line-height:24px;margin:16px 0;color:rgb(0,0,0);"><strong>Attendance Code:</strong> ${attendanceCode}</p>
						<p style="font-size:14px;line-height:24px;margin:16px 0;color:rgb(0,0,0);"><strong>Login Email:</strong> ${students.email}</p>
						<p style="font-size:14px;line-height:24px;margin:16px 0;color:rgb(0,0,0);">For any queries, contact us at <a href="tel:7042912701">+91-7042912701</a></p>
						<p style="font-size:14px;line-height:24px;margin:16px 0;color:rgb(0,0,0);"><strong>Address:</strong> 2nd &amp; 3rd Floor, Skyline Tower, Adarsh Nagar Samastipur</p>
					</div>
				</body>
				</html>
			`;

			try {
				await sendEmail({
					to: students.email,
					subject: 'Welcome to BookBuddy - Registration Successful',
					html: welcomeEmailHTML,
				});
			} catch (emailError) {
				console.error('Failed to send welcome email:', emailError);
			}

			history("/login");

		} catch (error) {
			console.error("Registration error:", error);
			showAlert(`Error: ${error instanceof Error ? error.message : "Registration failed. Please try again."}`, "danger");
			setShowSpinner(false);
		}
	}

	const onChange = (e) => {
		if (e.target.type === 'file') {
			setstudents({ ...students, [e.target.name]: e.target.files[0] });
		} else if (e.target.name === 'gender') {
			setstudents({ ...students, gender: e.target.value });
		} else {
			setstudents({ ...students, [e.target.name]: e.target.value });
		}

		// Clear error when user starts typing
		if (errors[e.target.name]) {
			setErrors({ ...errors, [e.target.name]: "" });
		}

		// Handle real-time validation
		if (e.target.name === 'name') {
			handleNameChange(e.target.value);
		} else if (e.target.name === 'password') {
			handlePasswordChange(e.target.value);
		} else if (e.target.name === 'address') {
			handleAddressChange(e.target.value);
		} else if (e.target.name === 'phone') {
			handlePhoneChange(e.target.value);
		} else if (e.target.name === 'email') {
			handleEmailChange(e.target.value);
		} else if (e.target.name === 'parentsphone') {
			handleParentPhoneChange(e.target.value);
		}
	};

	// Real-time validation functions
	const handleNameChange = (name) => {
		if (!name.trim()) {
			setErrors({ ...errors, name: "Full name is required" });
		} else if (name.trim().length < 2) {
			setErrors({ ...errors, name: "Name must be at least 2 characters" });
		} else if (!/^[a-zA-Z\s]+$/.test(name.trim())) {
			setErrors({ ...errors, name: "Name can only contain letters and spaces" });
		} else {
			setErrors({ ...errors, name: "" });
		}
	};

	const handlePasswordChange = (password) => {
		if (!password) {
			setErrors({ ...errors, password: "Password is required" });
		} else if (password.length < 6) {
			setErrors({ ...errors, password: "Password must be at least 6 characters" });
		} else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
			setErrors({ ...errors, password: "Password must contain at least one uppercase letter, one lowercase letter, and one number" });
		} else {
			setErrors({ ...errors, password: "" });
		}
	};

	const handleAddressChange = (address) => {
		if (!address.trim()) {
			setErrors({ ...errors, address: "Address is required" });
		} else if (address.trim().length < 10) {
			setErrors({ ...errors, address: "Please provide a complete address (at least 10 characters)" });
		} else {
			setErrors({ ...errors, address: "" });
		}
	};


	// Debounced phone number check function =====================================
	const [checkPhoneAvailability, setCheckPhoneAvailability] = useState(null);
	useEffect(() => {
		const timeout = setTimeout(async () => {
			if (checkPhoneAvailability && checkPhoneAvailability.length === 10) {
				const response = await fetch(`${host}/students/check-phone`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ phone: checkPhoneAvailability }),
				});
				const data = await response.json();
				setIsPhoneAvailable(data.available);
				setIsCheckingPhoneAvailability(false);

			}
		}, debounceDelay);

		return () => clearTimeout(timeout);
	}, [checkPhoneAvailability, debounceDelay, host]);

	const handlePhoneChange = (phone) => {
		const phoneRegex = /^\d{10}$/; // phone regex
		if (!phoneRegex.test(phone)) {
			setErrors({ ...errors, phone: "Please enter a valid phone number." });
			setIsCheckingPhoneAvailability(false)
			return; // Exit the function if validation fails
		}
		setIsCheckingPhoneAvailability(true);
		setCheckPhoneAvailability(phone); // Trigger debounced check
		setErrors({ ...errors, phone: "" });
	};

	// Debounced email check function
	const [checkEmailAvailability, setCheckEmailAvailability] = useState(null);
	useEffect(() => {
		const timeout = setTimeout(async () => {
			if (checkEmailAvailability) {
				const response = await fetch(`${host}/students/check-email`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ email: checkEmailAvailability }),
				});
				const data = await response.json();
				setIsEmailAvailable(data.available);
				setIsCheckingEmailAvailability(false);
			}
		}, debounceDelay);

		return () => clearTimeout(timeout);
	}, [checkEmailAvailability, debounceDelay, host]);

	const handleEmailChange = (email) => {
		const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/; // email regex
		if (!emailRegex.test(email)) {
			setErrors({ ...errors, email: "Please enter a valid email address." });
			setIsCheckingEmailAvailability(false)
			return; // Exit the function if validation fails
		}
		setIsCheckingEmailAvailability(true);
		setCheckEmailAvailability(email); // Trigger debounced check
		setErrors({ ...errors, email: "" });
	};


	const generatePassword = (inputId) => {
		const generatedPassword = generateStrongPassword();
		document.getElementById(inputId).value = generatedPassword;
		setstudents({ ...students, password: generatedPassword });
		copyToClipboard("passwordInput")
		showAlert('Strong password generated and copied to clipboard!', "success")

	}
	function togglePasswordVisibility(inputId) {
		const passwordInput = document.getElementById(inputId);
		const isPasswordVisible = passwordInput.type === "text";

		if (isPasswordVisible) {
			// Hide password
			passwordInput.type = "password";
		} else {
			// Show password
			passwordInput.type = "text";
		}
	}

	const handleParentPhoneChange = (eventOrValue) => {
		const phoneRegex = /^\d{10}$/; // Replace with your preferred phone number regex
		
		// Handle both cases: when called with event object or just the value
		let value;
		let event;
		
		if (typeof eventOrValue === 'string') {
			value = eventOrValue;
			event = null;
		} else {
			value = eventOrValue.target.value;
			event = eventOrValue;
		}
		
		if (!value) {
			setErrors({ ...errors, parentsphone: "Parent/Guardian phone is required" });
		} else if (!phoneRegex.test(value)) {
			setErrors({ ...errors, parentsphone: "Please enter a valid 10-digit phone number" });
		} else if (value === students.phone) {
			setErrors({ ...errors, parentsphone: "Parent phone number should be different from your phone number" });
		} else {
			setErrors({ ...errors, parentsphone: "" });
		}
		
		// Call the existing onChange function only if we have an event object
		if (event) {
			onChange(event);
		}
	};



	return (

		<>
			<div className="container d-flex flex-column">
				<div className="row h-100">
					<div className="col-sm-10 col-md-8 col-lg-6 col-xl-5 mx-auto pt-3 d-table h-100">
						<div className="d-table-cell align-middle">

							<div className="text-center mt-4">
								<h1 className="h2">Get started</h1>
								<p className="lead">
									Sign up to book a seat.
								</p>
							</div>

							<div className="card">
								<div className="card-body">
									<div className="m-sm-3">
										<form encType="multipart/form-data" onSubmit={handleClick} className="needs-validation" noValidate>
											<div className="mb-3">
												<label className="form-label">Full name <span className="text-danger">*</span></label>
												<input className={`form-control form-control-lg ${errors.name ? "is-invalid" : ""}`} value={students.name} type="text" name="name" placeholder="Enter your name" onChange={onChange} required />
												{errors.name && <div className="invalid-feedback">{errors.name}</div>}
											</div>
											<div className="mb-3">
												<label className="form-label">Email <span className="text-danger">*</span></label>
												<input className={`form-control form-control-lg ${errors.email ? "is-invalid" : ""} ${(isEmailAvailable ? "" : "is-invalid")}`} value={students.email} type="email" name="email" placeholder="Enter your email" onChange={onChange} required />
												{isCheckingEmailAvailability && <span className='spinner-wrap'><span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span></span>}
												{errors.email && <div id="email-error" class="invalid-feedback">{errors.email}</div>}
												{!isCheckingEmailAvailability && !errors.email && checkEmailAvailability && <div id="email-error" class={`${(isEmailAvailable ? "valid-feedback" : "invalid-feedback")}`}>{isEmailAvailable ? "Available" : "Email already exists, try a different one."}</div>}
											</div>
											<div className="mb-3">
												<label className="form-label">Create a password <span className="text-danger">*</span></label>
												<div className="position-relative">
													<input className={`form-control form-control-lg ${errors.password ? "is-invalid" : ""}`} id="passwordInput" value={students.password} type="password" name="password" placeholder="Enter a new password" onChange={onChange} required />
													<svg onClick={() => { generatePassword("passwordInput") }} xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" style={{ cursor: "pointer" }} className="bi bi-file-earmark-lock2-fill position-absolute top-50 end-0 translate-middle" viewBox="0 0 16 16"><path d="M7 7a1 1 0 0 1 2 0v1H7z" /><path d="M9.293 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.707A1 1 0 0 0 13.707 4L10 .293A1 1 0 0 0 9.293 0M9.5 3.5v-2l3 3h-2a1 1 0 0 1-1-1M10 7v1.076c.54.166 1 .597 1 1.224v2.4c0 .816-.781 1.3-1.5 1.3h-3c-.719 0-1.5-.484-1.5-1.3V9.3c0-.627.46-1.058 1-1.224V7a2 2 0 1 1 4 0" /></svg>
													<svg onClick={() => { togglePasswordVisibility("passwordInput") }} xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" style={{ cursor: "pointer" }} className="bi bi-eye-fill position-absolute top-50 end-0 translate-middle mx-4" viewBox="0 0 16 16"><path d="M10.5 8a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0" /><path d="M0 8s3-5.5 8-5.5S16 8 16 8s-3 5.5-8 5.5S0 8 0 8m8 3.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7" /></svg>
												</div>
												{errors.password && <div className="invalid-feedback">{errors.password}</div>}
											</div>
											<div className="mb-3">
												<label className="form-label">Gender <span className="text-danger">*</span></label>
												<div className="form-check form-check-inline mx-2">
													<input className="form-check-input" value="Male" type="radio" name="gender" id="flexRadioDefault1" checked={students.gender === 'Male'} onChange={onChange} required />
													<label className="form-check-label" htmlFor="Male">
														Male
													</label>
												</div>
												<div className="form-check form-check-inline">
													<input className="form-check-input" value="Female" type="radio" name="gender" id="flexRadioDefault2" checked={students.gender === 'Female'} onChange={onChange} required />
													<label className="form-check-label" htmlFor="Female">
														Female
													</label>
												</div>
												{errors.gender && <div className="invalid-feedback d-block">{errors.gender}</div>}
											</div>
											<div className="mb-3">
												<label className="form-label">Address <span className="text-danger">*</span></label>
												<input className={`form-control form-control-lg ${errors.address ? "is-invalid" : ""}`} value={students.address} type="text" name="address" placeholder="Enter your address" onChange={onChange} required />
												{errors.address && <div className="invalid-feedback">{errors.address}</div>}
											</div>
											<div className="mb-3">
												<label className="form-label">Phone No. <span className="text-danger">*</span></label>
												<input id="phone" className={`form-control form-control-lg ${errors.phone || !isPhoneAvailable ? 'is-invalid' : ''}`} value={students.phone} onChange={onChange} type="text" name="phone" placeholder="Your phone number" required />
												{isCheckingPhoneAvailability && <span className='spinner-wrap'><span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span></span>}
												<div id="phone-error" class="invalid-feedback">{errors.phone}</div>
												{!isCheckingPhoneAvailability && !errors.phone && checkPhoneAvailability && <div id="phone-error" class={`${(isPhoneAvailable ? "valid-feedback" : "invalid-feedback")}`}>{isPhoneAvailable ? "Available" : "Phone number already exists, try a different one."}</div>}
											</div>
											<div className="mb-3">
												<label className="form-label">Parent's Phone No. <span className="text-danger">*</span></label>
												<input className={`form-control form-control-lg ${errors.parentsphone ? "is-invalid" : ""}`} value={students.parentsphone} type="text" name="parentsphone" placeholder="Guardian or relative number" onChange={onChange} required />
												<div id="parentphone-error" class="invalid-feedback">{errors.parentsphone}</div>
											</div>
											<div className="mb-3">
												<label className="form-label">Photo <span className="text-danger">*</span></label>
												<input className={`form-control form-control-lg ${errors.photo ? "is-invalid" : ""}`} type="file" name="photo" placeholder="Upload your photo" onChange={onChange} required />
												<small className='px-1 bg-light'>Valid file types: jpg, jpeg, png, pdf, heic | Size: smaller than 5MB </small>
												{errors.photo && <div className="invalid-feedback">{errors.photo}</div>}
											</div>
											<div className="mb-3">
												<label className="form-label">Valid Id Proof <span className="text-danger">*</span></label>
												<input className={`form-control form-control-lg ${errors.documentid ? "is-invalid" : ""}`} type="file" name="documentid" placeholder="Upload your document" onChange={onChange} required />
												<small className='px-1 bg-light'>Valid file types: jpg, jpeg, png, pdf, heic | Size: smaller than 5MB </small>
												{errors.documentid && <div className="invalid-feedback">{errors.documentid}</div>}
											</div>

											<div className="d-grid gap-2 mt-3">
												<button type="submit" className="btn btn-lg btn-primary" disabled={students.password.length < 4 || students.phone.length < 10 || students.email.length < 6 || students.name.length < 3 || students.photo.length === 0 || students.documentid.length === 0 || isPhoneAvailable === false || isEmailAvailable === false}>{showSpinner ? <div class="spinner-border" role="status"></div> : "Sign up"}</button>
											</div>
										</form>
									</div>
								</div>
							</div>
							<div className="text-center mb-3">
								Already have account? <Link to="/login">Log In</Link>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	)
}

export default Signup